import { useSelector } from 'react-redux';

import { DEFAULT_LANGUAGE } from 'constants/LanguageConstants';
import { getLocale } from 'modules/i18n/selectors/data';
import { getDealerDefaultLanguage } from 'selectors/dealerSelectors';

/**
 * @param {{ locale?: string, dealerLang?: string }} [options]
 *
 * Translate user-defined labels (as fb-translate used to do in v1)
 * @description Rules to follow (from https://fleetback.atlassian.net/wiki/x/ugodAQ):
 * - Use the language asked. For example for an element in the interface use Fleetback language, for a predefined message use the client language;
 * - If not exist, use the dealership language;
 * - If not exist, use English;
 * - If not exist, use first language available.
 * */
const useTranslate = ({ locale, dealerLang } = {}) => {
  const defaultLocale = useSelector(getLocale);
  const dealerLanguage = useSelector(getDealerDefaultLanguage);

  return {
    /**
     * @param {import('modules/global/typing/common/Label').Label} label
     * @returns {string}
     */
    translate(label) {
      // language <=> translation pairs
      const translations = Object.entries(label);
      // Lower-cased language names
      const availableLanguages = translations.map(([language]) => language.toLowerCase());
      // Order of languages to try
      const targetLanguages = [locale, dealerLang, defaultLocale, dealerLanguage, DEFAULT_LANGUAGE, availableLanguages[0]].reduce(
        (acc, language) => (language && !acc.includes(language.toLowerCase()) ? [...acc, language.toLowerCase()] : acc),
        []
      );

      let translation;
      // Get the first available translation
      do {
        const targetLanguage = targetLanguages.shift();
        [, translation] = translations.find(([language]) => language.toLowerCase() === targetLanguage) ?? [];
      } while (!translation && targetLanguages.length > 0);

      return translation;
    },
  };
};

export default useTranslate;
